import React from 'react';
import {BotFormSelectionProvider, BotRequest, BotRequestPartial} from "../BotForm";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {Section} from "../../../../../elements/section/Section";
import styles from "../BotForm.module.css";
import {
    PositiveIntegerNumberValidator,
    validateUrlProcessorMatchField,
    validateUrlProcessorMatchType
} from "../../../../../elements/validator/Validator";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";
import {
    CustomSwitchFieldWithTitle
} from "../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {MatchField, MatchType} from "../../../../../../api/generated";
import {ValidatedSelectField} from "../../../../../elements/validator/component/ValidatedSelectField";
import {useAppSelector} from "../../../../../../app/hooks";
import {allMatchFieldOptions, allMatchTypeOptions} from "./util/urlProcessorUtil";
import {
    getAssistantVehicleDataProvider,
    hasAssistantVehicleDataProvider
} from "../../../../../../utils/assistantVehicleDataProvider";
import {deriveLocationsFromFranchises} from "../../../franchiseTypes";

interface Props<T> {
    getMessage: (s: any) => any;
    updateRequest: (update: BotRequestPartial) => void;
    selectionProvider: BotFormSelectionProvider;
    updateValidationStatus: (status: ValidationResult<T>) => void,
}

export const UrlProcessor = <T extends BotRequest>({
                                                       getMessage,
                                                       updateRequest,
                                                       selectionProvider,
                                                       updateValidationStatus
                                                   }: Props<T>) => {

    const urlProcessorEnabled = useAppSelector(selectionProvider.urlProcessorEnabled)
    const selectedFranchises = useAppSelector(selectionProvider.franchiseList)
    const selectedLocations = deriveLocationsFromFranchises(selectedFranchises)
    const assistantVehicleDataProvider = getAssistantVehicleDataProvider(selectedLocations)
    const assistantHasVehicleDataProvider = hasAssistantVehicleDataProvider(selectedLocations)

    const matchFieldValidator = (_fieldName: string, value?: MatchField) => {
        return validateUrlProcessorMatchField(value, assistantVehicleDataProvider, assistantHasVehicleDataProvider);
    };

    const matchTypeValidator = (_fieldName: string, value?: MatchType) => {
        return validateUrlProcessorMatchType(value, assistantVehicleDataProvider, assistantHasVehicleDataProvider);
    };

    return <Section title={"URL Processor"}>
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"URL processor"}
            description={"Enable processing to try focus vehicle by URL"}
            valueSelector={selectionProvider.urlProcessorEnabled}
            toggleValue={(v) => updateRequest({urlProcessorEnabled: v})}
        />
        {urlProcessorEnabled && <>
            <div className={styles.fieldsLineWithMargin}>
                <ValidatedSelectField<MatchField, T>
                    required={urlProcessorEnabled}
                    onChange={(v) => updateRequest({matchField: v})}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'matchField'}
                    selector={selectionProvider.matchField}
                    validator={matchFieldValidator}
                    label={'Field'}
                    getMessage={getMessage}
                    options={allMatchFieldOptions}
                />
                <ValidatedSelectField<MatchType, T>
                    required={urlProcessorEnabled}
                    onChange={(v) => updateRequest({matchType: v})}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'matchType'}
                    selector={selectionProvider.matchType}
                    validator={matchTypeValidator}
                    label={'Match Type'}
                    getMessage={getMessage}
                    options={allMatchTypeOptions}
                />
            </div>
            <div className={styles.fieldsLineWithMargin}>
                <ValidatedInputField<number, T>
                    required={urlProcessorEnabled}
                    onChange={(v) => updateRequest({segmentNumberFromTheEnd: v})}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'segmentNumberFromTheEnd'}
                    selector={selectionProvider.segmentNumberFromTheEnd}
                    validator={PositiveIntegerNumberValidator}
                    label={'Segment Number From The End'}
                    placeholder={"e.g., 2"}
                    getMessage={getMessage}
                />
            </div>
        </>}
    </Section>
}
